import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Modal, InputGroup, Alert, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faEdit, 
  faTrash, 
  faUsers,
  faFileAlt,
  faEye,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { getCustomers, addCustomer, updateCustomer, deleteCustomer } from '../apis/customers';

const CustomersUpdated = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addError, setAddError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [customerForm, setCustomerForm] = useState({
    client_name: '',
    phone_number: '',
    site_name: '',
    site_address: '',
    pincode: ''
  });

  // Fetch customers from API
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCustomers();
      setCustomers(data);
    } catch (err) {
      setError('Failed to fetch customers');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data when component mounts
  useEffect(() => {
    fetchCustomers();
  }, []);

  const filteredCustomers = customers
    .filter(customer => {
      const matchesSearch = !searchTerm || 
        customer.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPhone = !phoneFilter || 
        customer.phone_number?.includes(phoneFilter) ||
        customer.phone?.includes(phoneFilter);
      const matchesCity = !cityFilter || 
        customer.city?.toLowerCase().includes(cityFilter.toLowerCase());
      
      return matchesSearch && matchesPhone && matchesCity;
    })
    .sort((a, b) => {
      // Sort by ID descending (latest customer added first)
      // If created_at timestamp exists, use that instead: new Date(b.created_at) - new Date(a.created_at)
      return (b.id || 0) - (a.id || 0);
    });

  const resetFilters = () => {
    setSearchTerm('');
    setPhoneFilter('');
    setCityFilter('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCustomer = async () => {
    // Validate required fields (phone_number and pincode optional)
    if (!customerForm.client_name || !customerForm.site_name || !customerForm.site_address) {
      setAddError('Please fill in all required fields.');
      return;
    }
    // Optional validations if provided
    if (customerForm.phone_number && !/^\d{10}$/.test(customerForm.phone_number)) {
      setAddError('Phone number must be 10 digits.');
      return;
    }
    if (customerForm.pincode && !/^\d{6}$/.test(customerForm.pincode)) {
      setAddError('Pincode must be 6 digits.');
      return;
    }
    
    setIsSubmitting(true);
    setAddError(null);
    
    try {
      await addCustomer(customerForm);
      // Refresh the data after adding
      await fetchCustomers();
      setShowAddModal(false);
      setAddError(null);
      setCustomerForm({
        client_name: '',
        phone_number: '',
        site_name: '',
        site_address: '',
        pincode: ''
      });
      // Show success toast
      setSuccessMessage('Customer added successfully!');
      setShowSuccessToast(true);
    } catch (error) {
      console.error('Error adding customer:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setAddError(error.response.data.error);
      } else {
        setAddError('Failed to add customer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerForm({
      client_name: customer.client_name || customer.name || '',
      phone_number: customer.phone_number || customer.phone || '',
      site_name: customer.site_name || '',
      site_address: customer.site_address || customer.address || '',
      pincode: customer.pincode || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;
    
    // Validate required fields (phone_number and pincode optional)
    if (!customerForm.client_name || !customerForm.site_name || !customerForm.site_address) {
      setEditError('Please fill in all required fields.');
      return;
    }
    // Optional validations if provided
    if (customerForm.phone_number && !/^\d{10}$/.test(customerForm.phone_number)) {
      setEditError('Phone number must be 10 digits.');
      return;
    }
    if (customerForm.pincode && !/^\d{6}$/.test(customerForm.pincode)) {
      setEditError('Pincode must be 6 digits.');
      return;
    }
    
    setIsSubmitting(true);
    setEditError(null);
    
    try {
      await updateCustomer(selectedCustomer.id, customerForm);
      // Refresh the data after updating
      await fetchCustomers();
      setShowEditModal(false);
      setEditError(null);
      setSelectedCustomer(null);
      setCustomerForm({
        client_name: '',
        phone_number: '',
        site_name: '',
        site_address: '',
        pincode: ''
      });
      // Show success toast
      setSuccessMessage('Customer updated successfully!');
      setShowSuccessToast(true);
    } catch (error) {
      console.error('Error updating customer:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setEditError(error.response.data.error);
      } else {
        setEditError('Failed to update customer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedCustomer) return;
    
    setIsSubmitting(true);
    setDeleteError(null);
    
    try {
      await deleteCustomer(selectedCustomer.id);
      // Refresh the data after deleting
      await fetchCustomers();
      setShowDeleteModal(false);
      setDeleteError(null);
      setSelectedCustomer(null);
      // Show success toast
      setSuccessMessage('Customer deleted successfully!');
      setShowSuccessToast(true);
    } catch (error) {
      console.error('Error deleting customer:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setDeleteError(error.response.data.error);
      } else {
        setDeleteError('Failed to delete customer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid className="p-4">
      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showSuccessToast} 
          onClose={() => setShowSuccessToast(false)} 
          delay={3000} 
          autohide
          bg="success"
        >
          <Toast.Header>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            <strong className="me-auto">Success</strong>
          </Toast.Header>
          <Toast.Body>{successMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="text-white mb-0">
            <FontAwesomeIcon icon={faUsers} className="me-2" />
            Customer Management
          </h2>
          <p className="text-muted">Manage your customers and their information</p>
        </Col>
      </Row>

      {/* Search and Filter Card */}
      <Card className="card-vitrag mb-4" style={{ 
        backgroundColor: '#1C2333', 
        border: '1px solid #3A4553',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3} className="mb-3">
              <Form.Label className="text-white">Search Customer</Form.Label>
              <InputGroup>
                <InputGroup.Text style={{ backgroundColor: '#2A3441', borderColor: '#3A4553', color: '#FFFFFF' }}>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    backgroundColor: '#2A3441',
                    borderColor: '#3A4553',
                    color: '#FFFFFF'
                  }}
                />
              </InputGroup>
            </Col>
            <Col md={3} className="mb-3">
              <Form.Label className="text-white">Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Filter by phone..."
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
                style={{
                  backgroundColor: '#2A3441',
                  borderColor: '#3A4553',
                  color: '#FFFFFF'
                }}
              />
            </Col>
            <Col md={3} className="mb-3">
              <Form.Label className="text-white">City</Form.Label>
              <Form.Control
                type="text"
                placeholder="Filter by city..."
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                style={{
                  backgroundColor: '#2A3441',
                  borderColor: '#3A4553',
                  color: '#FFFFFF'
                }}
              />
            </Col>
            <Col md={2} className="mb-3">
              <Button
                variant="outline-secondary"
                onClick={resetFilters}
                className="w-100"
                style={{
                  backgroundColor: 'transparent',
                  borderColor: '#6C757D',
                  color: '#6C757D',
                  borderRadius: '6px',
                  fontWeight: '600',
                  padding: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#6C757D';
                  e.target.style.color = '#FFFFFF';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6C757D';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Reset
              </Button>
            </Col>
            <Col md={1} className="mb-3">
              <Button
                variant="primary"
                className="btn-vitrag-primary w-100"
                onClick={() => setShowAddModal(true)}
                style={{
                  backgroundColor: '#FF6B35',
                  borderColor: '#FF6B35',
                  borderRadius: '6px',
                  fontWeight: '600',
                  padding: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#E55A2B';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#FF6B35';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Add
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <FontAwesomeIcon icon={faUsers} className="me-2" />
          Error loading customers: {error}
        </Alert>
      )}

      {/* Customers Table */}
      <Card className="card-vitrag" style={{ 
        backgroundColor: '#1C2333', 
        border: '1px solid #3A4553',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        overflow: 'hidden'
      }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-white mt-3">Loading customers...</p>
            </div>
          ) : (
            <div className="table-responsive custom-table-scroll" style={{ 
              backgroundColor: '#1C2333', 
              borderRadius: '8px', 
              overflow: 'auto',
              maxHeight: '70vh',
              minHeight: '400px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#3A4553 #1C2333'
            }}>
              <Table className="table-vitrag mb-0" style={{ 
                backgroundColor: '#1C2333', 
                marginBottom: 0,
                borderCollapse: 'separate',
                borderSpacing: 0,
                minWidth: '1200px'
              }}>
                <thead style={{ backgroundColor: '#2A3441' }}>
                  <tr>
                    <th style={{ 
                      padding: '20px 15px', 
                      color: '#FFFFFF', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      borderBottom: '2px solid #3A4553',
                      borderRight: '1px solid #3A4553',
                      backgroundColor: '#2A3441'
                    }}>Sr. No.</th>
                    <th style={{ 
                      padding: '20px 15px', 
                      color: '#FFFFFF', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      borderBottom: '2px solid #3A4553',
                      borderRight: '1px solid #3A4553',
                      backgroundColor: '#2A3441'
                    }}>Client Name</th>
                    <th style={{ 
                      padding: '20px 15px', 
                      color: '#FFFFFF', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      borderBottom: '2px solid #3A4553',
                      borderRight: '1px solid #3A4553',
                      backgroundColor: '#2A3441'
                    }}>Phone Number</th>
                    <th style={{ 
                      padding: '20px 15px', 
                      color: '#FFFFFF', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      borderBottom: '2px solid #3A4553',
                      borderRight: '1px solid #3A4553',
                      backgroundColor: '#2A3441'
                    }}>Site Name</th>
                    <th style={{ 
                      padding: '20px 15px', 
                      color: '#FFFFFF', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      borderBottom: '2px solid #3A4553',
                      borderRight: '1px solid #3A4553',
                      backgroundColor: '#2A3441'
                    }}>Site Address</th>
                    <th style={{ 
                      padding: '20px 15px', 
                      color: '#FFFFFF', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      borderBottom: '2px solid #3A4553',
                      borderRight: '1px solid #3A4553',
                      backgroundColor: '#2A3441'
                    }}>Pincode</th>
                    <th style={{ 
                      padding: '20px 15px', 
                      color: '#FFFFFF', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      borderBottom: '2px solid #3A4553',
                      backgroundColor: '#2A3441'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer, index) => (
                    <tr key={customer.id} style={{ 
                      borderBottom: '1px solid #3A4553',
                      transition: 'background-color 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.closest('tr').style.backgroundColor = '#2A3441';
                    }}
                    onMouseLeave={(e) => {
                      e.target.closest('tr').style.backgroundColor = 'transparent';
                    }}>
                      <td style={{ 
                        padding: '15px', 
                        color: '#FFFFFF', 
                        borderRight: '1px solid #3A4553',
                        textAlign: 'center'
                      }}>{index + 1}</td>
                      <td style={{ 
                        padding: '15px', 
                        color: '#FFFFFF', 
                        borderRight: '1px solid #3A4553'
                      }}>{customer.client_name || customer.name || 'N/A'}</td>
                      <td style={{ 
                        padding: '15px', 
                        color: '#FFFFFF', 
                        borderRight: '1px solid #3A4553'
                      }}>{customer.phone_number || customer.phone || 'N/A'}</td>
                      <td style={{ 
                        padding: '15px', 
                        color: '#FFFFFF', 
                        borderRight: '1px solid #3A4553'
                      }}>{customer.site_name || 'N/A'}</td>
                      <td style={{ 
                        padding: '15px', 
                        color: '#FFFFFF', 
                        borderRight: '1px solid #3A4553'
                      }}>{customer.site_address || customer.address || 'N/A'}</td>
                      <td style={{ 
                        padding: '15px', 
                        color: '#FFFFFF', 
                        borderRight: '1px solid #3A4553'
                      }}>{customer.pincode || 'N/A'}</td>
                      <td style={{ padding: '15px', color: '#FFFFFF' }}>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditCustomer(customer)}
                            style={{
                              backgroundColor: 'transparent',
                              borderColor: '#007BFF',
                              color: '#007BFF',
                              borderRadius: '4px',
                              padding: '5px 10px',
                              fontSize: '12px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#007BFF';
                              e.target.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#007BFF';
                            }}
                          >
                            <FontAwesomeIcon icon={faEdit} className="me-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteCustomer(customer)}
                            style={{
                              backgroundColor: 'transparent',
                              borderColor: '#DC3545',
                              color: '#DC3545',
                              borderRadius: '4px',
                              padding: '5px 10px',
                              fontSize: '12px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#DC3545';
                              e.target.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#DC3545';
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add Customer Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton className="modal-header-vitrag">
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-content-vitrag">
          <Form>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Client Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="client_name"
                    value={customerForm.client_name}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter client name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={customerForm.phone_number}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter 10-digit number"
                    maxLength="10"
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Pincode</Form.Label>
                  <Form.Control
                    type="text"
                    name="pincode"
                    value={customerForm.pincode}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter pincode"
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Site Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="site_name"
                    value={customerForm.site_name}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter site name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Site Address *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="site_address"
                    value={customerForm.site_address}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter site address"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modal-footer-vitrag">
          {addError && (
            <Alert variant="danger" className="w-100 mb-3">
              {addError}
            </Alert>
          )}
          <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            className="btn-vitrag-primary" 
            onClick={handleAddCustomer}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              'Add Customer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Customer Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton className="modal-header-vitrag">
          <Modal.Title>Edit Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-content-vitrag">
          <Form>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Client Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="client_name"
                    value={customerForm.client_name}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter client name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={customerForm.phone_number}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter 10-digit number"
                    maxLength="10"
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Pincode</Form.Label>
                  <Form.Control
                    type="text"
                    name="pincode"
                    value={customerForm.pincode}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter pincode"
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Site Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="site_name"
                    value={customerForm.site_name}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter site name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Site Address *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="site_address"
                    value={customerForm.site_address}
                    onChange={handleInputChange}
                    className="form-control-vitrag"
                    placeholder="Enter site address"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modal-footer-vitrag">
          {editError && (
            <Alert variant="danger" className="w-100 mb-3">
              {editError}
            </Alert>
          )}
          <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            className="btn-vitrag-primary" 
            onClick={handleUpdateCustomer}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              'Update Customer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton className="modal-header-vitrag">
          <Modal.Title>Delete Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-content-vitrag">
          <Alert variant="warning">
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Are you sure you want to delete this customer? This action cannot be undone.
          </Alert>
          {selectedCustomer && (
            <div>
              <p><strong>Customer:</strong> {selectedCustomer.client_name || selectedCustomer.name}</p>
              <p><strong>Phone:</strong> {selectedCustomer.phone_number || selectedCustomer.phone}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-vitrag">
          {deleteError && (
            <Alert variant="danger" className="w-100 mb-3">
              {deleteError}
            </Alert>
          )}
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Customer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomersUpdated;